import { FunctionComponent, useEffect, useState } from "react";
import styled from "styled-components";
import { styles } from "../../styles/themes/style";
import Text from "../../components/Text";
import Input from "../../components/Input";
import Tag from "../../components/Tag";
import Button from "../../components/Button";
import Column from "../../components/Column";
import DueDate from "../DueDate";
import Reminder from "../Reminder";
import Select from "../../components/select/Select";
import CustomTextArea from "../../components/CustomTextAreaV2";
import ConnectionAutocompleteSelect from "../ConnectionAutocompleteSelect";
import { useTasksHandlers } from "../../services/actions/tasks/useTasksHandlers";
import { useAppHandlers } from "../../services/actions/app/useAppHandlers";
import { SidebarTypes } from "../../interfaces/store/appStore";
import { IConnection } from "../../interfaces/models/connection";
import { useRemindersHandlers } from "../../services/actions/reminders/useRemindersHandlers";
import { useWorkspaceHandlers } from "../../services/actions/workspace/useWorkspaceHandlers";
import { capitalLetters } from "../../utils";
import Row from "../../components/Row";
import ProfilePhoto from "../../components/ProfilePhoto";

interface ITasksSidebarProps {
    task?: any
    formType?: string
    connection?: IConnection
    selectedTasks?: any;
    onCloseHandler?: () => void
    onCreateHandler?: (taskId: string | null, task: any) => void
}

const CreateEditTaskForm: FunctionComponent<ITasksSidebarProps> = ({ task, formType = 'all', connection, selectedTasks, onCloseHandler, onCreateHandler}) => {
    const editTask = task !== undefined

    const isAll = (formType === 'all' || formType === 'linked connection') ? true : false
    const isReminder = formType === 'reminder' ? true : false
    const isDueDate = formType === 'due date' ? true : false
    const isPriorityLevel = formType === 'priority level' ? true : false
    const isShortForm = formType === 'short' ? true : false
    const isLinkedConnection = formType === 'linked connection' ? true : false

    const priorityOptions = [
        { value: 0, label: 'No Priority Level', component: <Text $lighter>No Priority Level</Text> },
        { value: 1, label: 'Low', component: <Tag title="Low" $color={styles.colors.blue600} /> },
        { value: 2, label: 'Medium', component: <Tag title="Medium" $color={styles.colors.green500} /> },
        { value: 3, label: 'High', component: <Tag title="High" $color={styles.colors.orange600} /> },
        { value: 4, label: 'Urgent', component: <Tag title="Urgent" $color={styles.colors.red600} /> }
    ]

    const typeOptions = [
        { value: 'No Type', label: 'No Type' },
        { value: 'General', label: 'General' },
        { value: 'Deadline', label: 'Deadline' },
        { value: 'In-person meeting', label: 'In-person meeting' },
        { value: 'Online meeting', label: 'Online meeting' },
        { value: 'Email', label: 'Email' },
        { value: 'Call', label: 'Call' },
        { value: 'Book a meeting', label: 'Book a meeting' },
        { value: 'LinkedIn message', label: 'LinkedIn message' },
        { value: 'LinkedIn contact add', label: 'LinkedIn contact add' }
    ]

    const { updateTaskHandler, createTaskHandler, bulkUpdateTasksHandler } = useTasksHandlers()
    const { closeSidebar } = useAppHandlers()
    const { bulkAddRemindersHandler } = useRemindersHandlers()
    const { getWorkspaceUsersPublicHandler } = useWorkspaceHandlers()

    const [title, setTitle] = useState<string>(editTask ? task.title : '')
    const [assignee, setAssignee] = useState<any | undefined>(
        editTask && task.assigned.length ? {
            label: <Text $label>{capitalLetters(task.assigned[0], true)}</Text>,
            value: task.assigned[0]
        } : undefined)
    const [linkedConnection, setLinkedConnection] = useState<IConnection | undefined>(() => {
        if (editTask && task.associations?.connections?.length) {
            const conn = task.associations.connections[0];
            // Check if connection has profile wrapper (from API response) or is already flattened
            if (conn.profile) {
                // Connection has profile wrapper - flatten it
                return {
                    ...conn.profile,
                    _id: conn._id,
                    tags: conn.tags,
                    notes: conn.notes,
                    autoTagsArray: conn.autoTagsArray ?? [],
                };
            } else {
                // Connection is already flattened - use directly
                return conn;
            }
        }
        return isLinkedConnection ? connection : undefined;
    })
    const [dueDate, setDueDate] = useState<any>(editTask ? (
        {
            end: task.end,
            repeating: task.repeating
        }
    ) : undefined)
    const [reminder, setReminder] = useState<any>(editTask && task?.reminders?.length ? task?.reminders?.[0].end : undefined)
    const [priority, setPriority] = useState<any>(editTask && task?.priority !== 0 && priorityOptions.find((option: any) => option.value === task?.priority))
    const [type, setType] = useState<any>(editTask && typeOptions.find((option: any) => option.value === task?.type && task?.type !== 'No Type'))
    const [description, setDescription] = useState<string>(editTask ? task.value : '')
    const [ workspaceUsers, setWorkspaceUsers ] = useState<any>([])

    const getWorkspaceUsersPublic = async () => {
        const result = await getWorkspaceUsersPublicHandler()
        setWorkspaceUsers(result.response ?? [])
    }
    
    const cancelHandler = () => {
        closeSidebar(SidebarTypes.TASK_EDIT_CREATE)
        onCloseHandler && onCloseHandler();
    }

    const saveHandler = () => {
        closeSidebar(SidebarTypes.TASK_EDIT_CREATE)

        // Prepare connection for API - backend expects the structure with profile wrapper
        // Based on the payload structure shown, backend expects connection with profile wrapper
        const prepareConnectionForAPI = (conn: IConnection | undefined) => {
            if (!conn || !conn._id) return undefined;
            // If connection already has profile structure (from API), use it as is
            if ((conn as any).profile) {
                return conn;
            }
            // Extract profile fields and wrap them in profile structure
            // Keep connection-level fields (_id, tags, notes, autoTagsArray) at top level
            const { _id, tags, notes, autoTagsArray, ...profileFields } = conn;
            return {
                _id,
                profile: profileFields,
                tags: tags || [],
                notes: notes || [],
                autoTagsArray: autoTagsArray || [],
            };
        };

        let body = {
            title: title,
            assigned: assignee ? [assignee.value] : [],
            associations: { 
                connections: linkedConnection 
                    ? [prepareConnectionForAPI(linkedConnection)].filter(Boolean) 
                    : (connection ? [prepareConnectionForAPI(connection)].filter(Boolean) : []) 
            },
            end: !!dueDate?.end ? dueDate?.end : 0,
            repeating: { unit: dueDate?.repeating?.unit ?? 'Never', value: 1, active: true },
            priority: priority ? priority.value : 0,
            type: type ? type.label : 'No Type',
            reminder: reminder ? reminder : false,
            value: description ?? ''
        }

        if(isDueDate) {
            bulkUpdateTasksHandler({
                end: !!dueDate?.end ? dueDate?.end : 0,
                repeating: { unit: dueDate?.repeating?.unit ?? 'Never', value: 1, active: true },
                ids: selectedTasks
            })
        } else if(isPriorityLevel) {
            bulkUpdateTasksHandler({priority: priority?.value, ids: selectedTasks})
        } else if(isReminder) {
            bulkAddRemindersHandler({reminder: reminder ? reminder : false, selectedTasksIds: selectedTasks})
        } else if(isShortForm) {
            onCreateHandler && onCreateHandler(task?._id, body)
        } else if(editTask) {
            updateTaskHandler(task._id, body)
        }  else {
            createTaskHandler(body)
        }
    }

    useEffect(() => {
        getWorkspaceUsersPublic()
    }, [])

    return (
        <Column gap="10px">
            {(isAll || isShortForm) && <section>
                <Text $label>{`Task Name ${title.length === 0 ? '(Required)' : ''}`}</Text>
                <Input placeholder="Enter task name" prefilledValue={title} onChangeHandler={(e: any) => setTitle(e)} />
            </section>}
            {(isAll || isShortForm) && <section>
                <Text $label>Assignee</Text>
                <Select
                    placeholder="Enter assignee name"
                    isSearchable={false}
                    options={workspaceUsers.map((workspaceUser: any) => ({
                        value: workspaceUser,
                        label: 
                        <Row gap="6px" alignItems>
                            <ProfilePhoto tooltipMessage="" capitalLetters={capitalLetters(workspaceUser)} />
                            <Column>
                                {<Text $label>{capitalLetters(workspaceUser, true)}</Text>}
                                <Text $lighter>{workspaceUser.email}</Text>
                            </Column>
                        </Row>
                    }))}
                    value={assignee}
                    onChange={(e: any) => setAssignee({ label: <Text $label>{capitalLetters(e.value, true)}</Text>, value: e.value} )}
                />
            </section>}
            {isAll && <section>
                <Text $label>Linked contact</Text>
                <ConnectionAutocompleteSelect
                    width={"296px"}
                    prefilledValue={linkedConnection}
                    onChangeHandler={(e: any) => setLinkedConnection(e)}
                />
            </section>}
            {(isAll || isDueDate || isShortForm) && <section>
                <Text $label>Due date</Text>
                <DueDate
                    end={dueDate?.end}
                    repeating={dueDate?.repeating}
                    onChangeHandler={(e: any) => setDueDate(e)}
                />
            </section>}
            {(isAll || isReminder || isShortForm) && <section>
                <Text $label>Reminder</Text>
                <Reminder
                    end={reminder}
                    onChangeHandler={(e: any) => setReminder(e)}
                />
            </section>}
            {(isAll || isPriorityLevel) && <section>
                <Text $label>Priority level</Text>
                <Select
                    $components
                    placeholder="No priority level"
                    isSearchable={false}
                    options={priorityOptions}
                    value={priority}
                    onChange={(e: any) => setPriority(e)}
                />
            </section>}
            {isAll && <section>
                <Text $label>Type</Text>
                <Select
                    placeholder="No type"
                    isSearchable={false}
                    options={typeOptions}
                    value={type}
                    onChange={(e: any) => setType(e)}
                />
            </section>}
            {isAll && <section>
                <Text $label>Task description</Text>
                <CustomTextArea
                    maxLength={400}
                    placeholder='Enter description'
                    value={description ?? ''}
                    onChange={(e: any) => setDescription(e.target.value)}
                />
            </section>}
            <ButtonWrapper>
                <Button $type="cancel" onClickHandler={cancelHandler}>Cancel</Button>
                <Button $type="blue" disabled={(isAll || isShortForm) ? title.length === 0 : false} onClickHandler={saveHandler}>Save</Button>
            </ButtonWrapper>
        </Column>
    )
}

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: end;
    gap: 10px;
`
export default CreateEditTaskForm